import { AuxDatePickerDateFormatEnum, AuxDatePickerSeparatorsEnum } from "@blk/aladdin-web-components";
import { trim } from "lodash";
import moment, { locale, localeData, utc } from "moment";
import { regions } from "./regions";
import { userPreferencesDefaults } from "./user-preferences-defaults";
import { dateFormats } from "components/modal/settings/date-formats";
import { formatDateString } from "components/ux/ins-date-pickers";
import { useGetUserPreferences } from "hooks/use-get-user-preferences";
import { appStore } from "index";
import { DataGranularity } from "store/current-dashboard/types";
// Separator is unlinked to format in ADS
export const insDateDefaultSeparator = AuxDatePickerSeparatorsEnum.FORWARD_SLASH;
export const dateFormatActualSeparators = () => {
    const separatorList = [];
    Object.values(AuxDatePickerDateFormatEnum).forEach((v) => {
        v.match("/[^A-Z]/g")?.forEach((match) => separatorList.push(match));
    });
    const onlyUnique = (value, index, array) => array.indexOf(value) === index;
    return separatorList.filter(onlyUnique);
};
export const getDateWhenString = (date) => {
    let newDate = date;
    if (typeof date === "string") {
        newDate = new Date(date);
    }
    return newDate;
};
const manageTimeZone = (date) => {
    const vDate = getDateWhenString(date);
    return vDate.getTimezoneOffset() < 0
        ? vDate.setUTCFullYear(vDate.getFullYear(), vDate.getMonth(), vDate.getDate())
        : vDate.setUTCFullYear(vDate.getUTCFullYear(), vDate.getUTCMonth(), vDate.getUTCDate());
};
export const getLocalFormat = () => {
    const userLocale = window.navigator.language;
    locale(userLocale);
    return localeData().longDateFormat("L")
        .replaceAll("/", " ");
};
export const useGetDateFormat = () => {
    const [userPreferences] = useGetUserPreferences();
    const region = userPreferences?.region ?? userPreferencesDefaults.region;
    return regions[region]?.datePickerDateFormat ?? getLocalFormat();
};
export const getYearWeek = (date) => moment(date).week();
export const tryParseUtcDate = (value, dateFormat) => {
    const newDateTimestamp = utc(value, dateFormat.replaceAll(" ", "/"));
    if (newDateTimestamp.isValid()) {
        return {
            isValid: () => true,
            toDate: () => newDateTimestamp.toDate()
        };
    }
    return {
        isValid: () => false,
        toDate: () => new Date()
    };
};
export const getDateString = (date, format) => {
    const vDate = getDateWhenString(date);
    const settings = appStore.getState().settings.current;
    const dateFormat = settings.dateFormat ?? userPreferencesDefaults.dateFormat;
    const region = settings.region ?? userPreferencesDefaults.region;
    const formatter = dateFormats[dateFormat]?.format;
    if (formatter && vDate && region && !format) {
        manageTimeZone(vDate);
        vDate.setUTCFullYear(vDate.getUTCFullYear(), vDate.getUTCMonth(), vDate.getUTCDate());
        vDate.setUTCHours(0, 0, 0, 0);
        return formatter(vDate, region);
    }
    return moment(vDate).format(format ?? getLocalFormat());
};
export const getTimeString = (date, includeSeconds = false) => {
    const vDate = getDateWhenString(date);
    const settings = appStore.getState().settings.current;
    const region = settings.region ?? userPreferencesDefaults.region;
    const formatter = (value, regionCode) => new Intl.DateTimeFormat(regionCode, { hour: "numeric", minute: "numeric", second: includeSeconds ? "numeric" : undefined }).format(value);
    if (!vDate) {
        return null;
    }
    manageTimeZone(vDate);
    vDate.setUTCFullYear(vDate.getUTCFullYear(), vDate.getUTCMonth(), vDate.getUTCDate());
    vDate.setUTCHours(vDate.getUTCHours(), vDate.getUTCMinutes(), vDate.getUTCSeconds(), vDate.getUTCMilliseconds());
    return formatter(vDate, region);
};
export const getDatePickerString = (date) => {
    const settings = appStore.getState().settings.current;
    const region = settings.region ?? userPreferencesDefaults.region;
    const currentDate = date ?? new Date();
    const { datePickerDateFormat } = regions[region];
    if (datePickerDateFormat) {
        return moment(currentDate).format(datePickerDateFormat)
            .replaceAll(" ", insDateDefaultSeparator);
    }
    return moment(currentDate).format(getLocalFormat())
        .replaceAll(" ", insDateDefaultSeparator);
};
export const getDateAsStringForAuxDatePickerDateFormat = (date, format, separator) => {
    const separators = Object.values(AuxDatePickerSeparatorsEnum);
    if (date) {
        let formattedValue = moment(date).format(format);
        separators.forEach((sep) => {
            formattedValue = formattedValue.replaceAll(sep, separator);
        });
        return formattedValue;
    }
    return "";
};
export const getFormattedDateAlignWithUserSettings = (date) => {
    const { dateFormat, region } = appStore.getState().settings.current;
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    localDate.setMinutes(-localDate.getTimezoneOffset());
    const result = formatDateString(localDate, dateFormat, region);
    return `${result} \u3000`;
};
export const getFormatedDateFromString = (date) => {
    const settings = appStore.getState().settings.current;
    const { region } = settings;
    const currentDate = date ?? new Date();
    const { datePickerDateFormat } = regions[region];
    if (datePickerDateFormat) {
        return moment(currentDate).format(datePickerDateFormat);
    }
    return moment(currentDate).format(getLocalFormat());
};
export const getPreviousDate = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    d.setDate(d.getDate() - 1);
    return d;
};
export const getDateQuarter = (date) => Math.floor(date.getMonth() / 3) + 1;
export const formatYearLabel = (date) => `${date.getFullYear()}`;
export const dateFromNullable = (date) => {
    if (date) {
        return new Date(date);
    }
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d;
};
export const dateFromObject = (value) => {
    const { year, month, day } = value || {};
    if (year && month && day) {
        const d = new Date();
        d.setUTCFullYear(year, month - 1, day);
        d.setUTCHours(0, 0, 0, 0);
        return getDateString(d);
    }
    return "";
};
export const getTimestampFromDateString = (key) => {
    if (!key) {
        return 0;
    }
    const d = new Date(key);
    manageTimeZone(d);
    d.setUTCHours(0, 0, 0, 0);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};
export const getStartOfYearFromDateStrng = (key) => {
    if (!key) {
        return 0;
    }
    const d = new Date(key);
    const year = d.getTimezoneOffset() < 0 ? d.getFullYear() : d.getUTCFullYear();
    return Date.UTC(year, 0, 1, 0, 0, 0);
};
export const getStartOfQuarterTimestamp = (quarterTimestamp) => {
    const date = new Date(quarterTimestamp);
    const month = date.getMonth();
    return Date.UTC(date.getFullYear(), month - (month % 3), 1);
};
export const getLocateDateTimezoneAgnostic = (date) => {
    if (!date || !date.getUTCFullYear) {
        return date;
    }
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};
export const getEndOfQuarter = (date) => {
    const quarter = getDateQuarter(date);
    const firstDate = new Date(date.getFullYear(), quarter * 3, 1);
    return getPreviousDate(firstDate);
};
export const isEndOfQuarter = (date) => {
    const endOfQuarter = getEndOfQuarter(date);
    return date.getFullYear() === endOfQuarter.getFullYear() &&
        date.getMonth() === endOfQuarter.getMonth() &&
        date.getDate() === endOfQuarter.getDate();
};
export const getNextDate = (date) => new Date(date.setDate(date.getDate() + 1));
export const getPreviousYear = (date) => new Date(date.setFullYear(date.getFullYear() - 1));
export const getStartDatePreviousQuarter = (date) => {
    const quarter = getDateQuarter(date) - 1;
    return new Date(date.getFullYear(), (quarter * 3) - 3, 1);
};
export const getStartQuarter = (date) => {
    const quarter = getDateQuarter(date) - 1;
    return new Date(date.getFullYear(), quarter * 3, 1);
};
export const getEndDateOfPreviousQuarter = (date) => {
    const startDatePreviousQuarter = getStartQuarter(date);
    return getPreviousDate(startDatePreviousQuarter);
};
export const getLastDateOfPreviousYear = (date) => {
    const december = 11, lastDay = 31;
    return getPreviousYear(new Date(date.getFullYear(), december, lastDay));
};
export const getLastYearOfCurrentDate = (date, yearsToGoBack) => {
    const currentYear = date.getUTCFullYear();
    const previousYear = currentYear - yearsToGoBack;
    const adjustedDate = new Date(date);
    adjustedDate.setUTCFullYear(previousYear);
    return adjustedDate;
};
export const getDate1String = (currentGranularity, asOfDate) => {
    let date1 = null;
    switch (currentGranularity) {
        case DataGranularity.quarterly:
            date1 = getDateString(getEndDateOfPreviousQuarter(new Date(asOfDate ?? 0)));
            break;
        case DataGranularity.yearOverYear:
            date1 = getDateString(getLastYearOfCurrentDate(new Date(asOfDate ?? 0), 1));
            break;
        default:
            date1 = getDateString(getLastDateOfPreviousYear(new Date(asOfDate ?? 0)));
            break;
    }
    return date1;
};
export const getDateAndTimeString = (date, includeSeconds = false) => {
    if (!date) {
        return "unknown";
    }
    const timeString = getTimeString(new Date(date), includeSeconds);
    const dateString = getDateString(new Date(date));
    const dateTime = `${dateString} at ${timeString}`;
    return dateTime;
};
export const dateForAuxDateColumnV2 = (value) => {
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = value.getDate();
    const formattedDate2 = { day, month, year };
    return formattedDate2;
};
export const trimDateString = (dateString) => trim(dateString);
export const getCurrentLocalTime = () => new Date().toISOString();
export const getLocalTimestamp = () => moment().format("YYYY_MM_DD__HH_mm_ss");
export const getUtcStartOfDayOrUndefined = (date) => (date ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)) : undefined);
